import React, { useEffect } from 'react'
// Bypasses @passport/store's package barrel (src/index.ts) on purpose — that barrel
// re-exports userStore.ts/roleStore.ts, which import '@admin/api', a private alias only
// admin-dashboard's own vite.config.ts declares. A bare '@passport/store' import works fine
// under Vite (every consuming app aliases it to a shim), but this package's own standalone
// `tsc` build does real Node resolution against the package's real (broken) dist/index.d.ts —
// see bizfirst-common-auth-react/runtime/passportStoreShim.ts for the full story. Importing
// the one needed store directly, same as that shim does, sidesteps the broken barrel entirely.
import { useAutoDetectTenantStore } from '../../../../passport/packages/@passport/store/src/autoDetectTenantStore'

export interface TenantNameProps {
  /** HTML element the name renders as. Defaults to `span`. */
  as?: keyof JSX.IntrinsicElements
  className?: string
  style?: React.CSSProperties
  /**
   * Rendered while no tenant business name is available (not yet resolved,
   * or the anonymous tenant-info lookup failed). Defaults to rendering
   * nothing, matching how callers already treated this before this
   * component existed (`{tenant?.businessName && <p>...}`).
   */
  fallback?: React.ReactNode
}

/**
 * Tenant-aware business name label — the one control every app should use
 * to show "the current tenant's name" instead of each app re-subscribing to
 * `useAutoDetectTenantStore` and re-triggering `loadTenant()` itself.
 *
 * Resolved anonymously from the request's own host (`GET
 * /api/v1/iam/tenants/info`, see `useAutoDetectTenantStore`) — no
 * session/JWT required, safe to render on pre-auth screens including a
 * failed-login fallback where the session itself couldn't be verified.
 * `loadTenant()` dedupes internally (5s window + localStorage persistence),
 * so mounting this in multiple places on the same page, or across repeated
 * mounts during a redirect loop, doesn't re-fetch on every mount.
 */
export function TenantName({ as: Tag = 'span', className, style, fallback = null }: TenantNameProps): React.ReactElement {
  const { tenant } = useAutoDetectTenantStore()

  useEffect(() => {
    if (!tenant) {
      useAutoDetectTenantStore.getState().loadTenant()
    }
  }, [tenant])

  if (!tenant?.businessName) {
    return <>{fallback}</>
  }

  return (
    <Tag className={className} style={style}>
      {tenant.businessName}
    </Tag>
  )
}

export default TenantName
